import React, { useState, useEffect, memo, useRef } from 'react';
import { Save, Upload, Trash2, TrendingUp, Settings, Undo, Redo, Code, Eye, SplitSquareHorizontal, RefreshCw } from 'lucide-react';
import MonacoEditor from '@/components/ui/monaco-editor';
import { EditorDynamicComponent } from './dynamicComponent';
import { Button } from '@/components/ui/button';
import ErrorBoundary from '@/components/errorBoundary';
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import Chat from "./Chat";
import { processLLMResponseAndUpdateState } from './process-llm.js';
import YAML from 'yaml';
import { FormProvider } from '@/components/form';
import { useForm } from '@tanstack/react-form';

import {
  ResizablePanel,
  ResizablePanelGroup,
  ResizableHandle,
} from '@/components/ui/resizable';

import OpenAI from 'openai';
import { Skeleton } from "@/components/ui/skeleton";
import APIConfigModal from './APIConfigModal.jsx';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { SelectFromValueList } from '@/components/ui/SelectFromValueList';
import { Textarea } from '@/components/ui/textarea';
import { TabsPanel } from '@/components/ui/tabs-panel';
import { SystemPromptPanel } from './system-prompt-panel';
import { DataPanel } from './data-panel';

import { useSnapshot } from 'valtio'
import { editorState } from './editor-state';
import { sessionState } from '@/app/sessionState';
import { post } from '@/utils/fetch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SpecificationPanel = () => {
  const { specification } = useSnapshot(editorState.component);

  return (
    <div className="flex flex-col h-full">
      <h3 className="font-semibold mb-2">Specification</h3>
      <Textarea
        value={specification}
        className="flex-1 w-full resize-none rounded-md border border-gray-200 p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 mb-6"
        placeholder="Specify the component you want to generate..."
        onChange={(e) => {
          editorState.updateSpecification(e.target.value);
        }}
      />
    </div>
  );
};

const editTabs = [
  { label: "Genie", value: "cmd", component: Chat },
  { label: "Specification", value: "specification", component: SpecificationPanel },
  { label: "Data", value: "data", component: DataPanel },
  { label: "System Prompt", value: "system-prompt", component: SystemPromptPanel },
];

const Preview = ({ text }) => {

  // poc: add empty form container to prototype form controls 
  const form = useForm({
    defaultValues: {  
      _: '',      
    },
    onSubmit: async ({ value }) => {
      console.log('Form submitted:', value)
    },
  });
      
  return (
    <ErrorBoundary key={text}>
      <FormProvider value={form}>
        <EditorDynamicComponent />
      </FormProvider>
    </ErrorBoundary>
  );
}

const EditorSkeleton = () => (
  <div className="space-y-2 p-4">
    <Skeleton className="h-[20px] w-[40%]" />
    <Skeleton className="h-[20px] w-[60%]" />
    <Skeleton className="h-[20px] w-[35%]" />
    <Skeleton className="h-[20px] w-[45%]" />
    <Skeleton className="h-[20px] w-[55%]" />
    <Skeleton className="h-[20px] w-[30%]" />
    <Skeleton className="h-[20px] w-[50%]" />
    <Skeleton className="h-[20px] w-[40%]" />
    <Skeleton className="h-[20px] w-[65%]" />
    <Skeleton className="h-[20px] w-[45%]" />
    <Skeleton className="h-[20px] w-[35%]" />
    <Skeleton className="h-[20px] w-[55%]" />
    <Skeleton className="h-[20px] w-[40%]" />
    <Skeleton className="h-[20px] w-[60%]" />
    <Skeleton className="h-[20px] w-[50%]" />
    <Skeleton className="h-[20px] w-[45%]" />
    <Skeleton className="h-[20px] w-[35%]" />
    <Skeleton className="h-[20px] w-[55%]" />
    <Skeleton className="h-[20px] w-[40%]" />
    <Skeleton className="h-[20px] w-[30%]" />
  </div>
);

const PlaygroundEditor = ({
  initialText = '',
  onChange = () => { },
  className = ''
}) => {
  const { jsx, specification } = useSnapshot(editorState.component);
  const { system: systemPromptText } = useSnapshot(editorState.prompts);
  const { undoDisabled, redoDisabled } = useSnapshot(editorState.toolbar);
  const { modelName, history, models } = useSnapshot(editorState);
  const { demoData } = useSnapshot(sessionState);
  const [fileName, setFileName] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [viewMode, setViewMode] = useState('both'); // 'editor', 'preview', 'both'

  const [isDarkMode, setIsDarkMode] = useState(
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );

  const [isAPIConfigModalOpen, setIsAPIConfigModalOpen] = useState(false);
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");


  // Notify parent component of changes
  const handleCodeChange = (newText = '') => {
    editorState.component.jsx = newText;
  };

  const handleSetArtifact = (content) => {
    // Check for wrapped prompt
    let pattern, match;
    let isYAML = false;
    content = content.replaceAll("\r", "");

    if (content.startsWith('/* ---')) {
      pattern = /\/\*\s*---[\r\n]+([\s\S]*?)[\r\n]+---\s*\*\/[\r\n]+([\s\S]*)/;
      isYAML = true;
    } else {
      pattern = /\/\*\s*<artifact-specification>[\r\n]+([\s\S]*?)[\r\n]+<\/artifact-specification>\s*\*\/[\r\n]+([\s\S]*)/;
    }

    match = content.match(pattern);

    if (match) {
      var [, extractedPrompt, extractedCode] = match;

      if (isYAML) {
        let frontmatter = YAML.parse(extractedPrompt);
        extractedPrompt = (frontmatter?.specification || '').trim();
      } else {
        extractedPrompt = extractedPrompt.trim();
      }
      editorState.updateSpecification(extractedPrompt);
      handleCodeChange(extractedCode);
    } else {
      // No prompt found, use entire content as code
      handleCodeChange(content);
    }
  };

  const handleAPIConfigModalOpen = () => {
    console.log('Opening API config modal');
    setIsAPIConfigModalOpen(true);
  };

  const handleAPIConfigModalClose = () => {
    setIsAPIConfigModalOpen(false);
  };

  // Add component lifecycle logging
  useEffect(() => {
    console.log('PlaygroundEditor mounted');
    return () => console.log('PlaygroundEditor unmounting');
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => setIsDarkMode(e.matches);

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    handleSetArtifact(initialText);
  }, [initialText]);



  const handleLoad = async () => {
    try {
      const [fileHandle] = await window.showOpenFilePicker({
        types: [
          {
            description: 'JSX Files',
            accept: {
              'text/jsx': ['.jsx']
            }
          }
        ]
      });

      const file = await fileHandle.getFile();
      setFileName(file.name);
      const content = await file.text();

      handleSetArtifact(content);
      editorState.clearHistory();
      const version = editorState.pushHistory(); // get version of current updated state

    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error loading file:', err);
      }
    }
  };

  const handleSaveFile = async () => {
    // Create wrapped prompt text
    // let wrappedPrompt = specification ? `/* <artifact-specification>\n\n${specification}\n\n</artifact-specification> */\n\n` : '';
    let spec = specification.replaceAll("\r", "");
    let yaml = YAML.stringify({ specification: spec }, { defaultKeyType: 'PLAIN', defaultStringType: 'BLOCK_FOLDED' });
    let frontmatter = `/* ---\n${yaml}--- */\n\n`;

    // Combine with existing content
    const contentToSave = frontmatter + jsx;

    try {
      // Create object with content and UTC timestamp
      const saveData = {
        content: contentToSave,
        timestamp: new Date().toISOString()
      };

      // Store as JSON string
      localStorage.setItem('artifact_wip', JSON.stringify(saveData));

      // File system save
      const handle = await window.showSaveFilePicker({
        suggestedName: fileName,
        types: [{
          description: 'JSX Files',
          accept: {
            'text/jsx': ['.jsx']
          }
        }]
      });

      // Get the file name chosen by user
      const newFileName = handle.name;

      // Create a writable stream and write the content
      const writable = await handle.createWritable();
      await writable.write(contentToSave);
      await writable.close();

      setFileName(newFileName); // Update state with new filename

      return false;

    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error saving file:', err);
        // Still save to localStorage even if file save fails
        localStorage.setItem('artifact_wip', contentToSave);
      }
      return false;
    }
  };

  const handleSaveAPI = async () => {

    var url = "/api/businessobjects/uiartifact/" + editorState.component.id + "?mode=extguid";
    try {
      // Prepare data to save
      const componentData = {
        title: fileName || "new component",
        properties: {
          artifact: {
            jsx: jsx,
            specification: specification,
          }
        }
      };

      // Make API call to save the component
      const response = await post(url, componentData);

    } catch (error) {
      console.error('Error saving component:', error);
      setErrorMessage(error.message || 'An error occurred while saving the component');
      setIsErrorDialogOpen(true);
      return false;
    }
  };

  const handleSave = async () => {
    if (editorState.component.id) {
      return handleSaveAPI();
    } else {
      return handleSaveFile();
    }
  };

  const handleClear = () => {
    handleCodeChange('');
    editorState.updateSpecification('');
    editorState.prompts.editCmd = '';
    editorState.clearHistory();
  };

  function wrapCommentBlock(input, maxLineLength = 70) {
    const words = input.split(' ');
    let lines = [];
    let currentLine = '';

    words.forEach(word => {
      if ((currentLine + word).length + 1 > maxLineLength) {
        lines.push(currentLine.trim());
        currentLine = '';
      }
      currentLine += word + ' ';
    });

    if (currentLine.trim()) {
      lines.push(currentLine.trim());
    }

    // Wrap the lines with comment block
    const commentBlock = '/*\n' +
      lines.map(line => `   ${line}`).join('\n') +
      '\n*/';
    return commentBlock;
  }


  const changeFavicon = (iconURL) => {
    const link = document.querySelector("link[rel~='icon']");
    if (!link) {
      const newLink = document.createElement('link');
      newLink.rel = 'icon';
      newLink.href = iconURL;
      document.head.appendChild(newLink);
    } else {
      link.href = iconURL;
    }
  };

  useEffect(() => {
    // Set default favicon on mount
    changeFavicon('/favicon-default.ico');

    // Add event listener to revert favicon on focus
    const handleFocus = () => changeFavicon('/favicon-default.ico');
    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const handleUndo = () => {
    editorState.undoHistory();
  };

  const handleRedo = () => { editorState.redoHistory() };

  const handleSelectHistoryVersion = (versionIndex) => {
    editorState.selectHistoryVersion(parseInt(versionIndex, 10));
  };


  const invokeLLM = async (userPrompt, modelName, mode) => {
    try {

      setIsGenerating(true);

      userPrompt = await appendJsonSample(userPrompt, mode);

      console.info(userPrompt);

      const response = await invokeOpenAI(
        [
          { role: "system", content: systemPromptText },
          { role: "user", content: userPrompt }
        ], modelName);

      if (!response) return;

      var provider = response.provider || "";
      console.log(provider, response);

      if (!response || !response.choices || !response.choices[0] || !response.choices[0].message) {
        console.error("Invalid LLM response structure received in processLLMResponseAndUpdateState", response);
        const error = new Error("Invalid LLM response structure from API");
        throw error;
      }

      if (response.error) {
        const errorMessage = (response.error.message || "Unknown LLM error").toString();
        const errorCode = response.error.code;
        const error = new Error(`LLM API Error: ${errorMessage}`);
        if (errorCode) {
          error.code = errorCode;
        }
        throw error;
      }

      if (response.choices[0].message.reasoning) {
        console.info("LLM reasoning:", response.choices[0].message.reasoning);
      }

      const result = response.choices[0].message.content;
      processLLMResponseAndUpdateState(result, editorState, setFileName, handleCodeChange);

    } catch (error) {
      // This catch block now handles errors from invokeOpenAI OR processLLMResponseAndUpdateState
      console.error("Error in invokeLLM:", error);
      const errorMessageString = error && error.message ? error.message : 'An error occurred during LLM operation';
      setErrorMessage(errorMessageString);
      setIsErrorDialogOpen(true);
    } finally {
      setIsGenerating(false);
    }
  };

  const invokeOpenAI = async (messages, currentModelName) => { // Renamed parameter for clarity

    const apiKey = localStorage.getItem('api-configuration-key');
    const apiUrl = localStorage.getItem('api-configuration-url');
    const defaultModelName = localStorage.getItem('api-configuration-model');
    let model = currentModelName || defaultModelName; // Use currentModelName passed from invokeLLM

    if (model === "_") model = defaultModelName;

    if (!apiKey || !defaultModelName) {
      handleAPIConfigModalOpen(); // Show settings modal
      return undefined;
    }

    const openai = new OpenAI({
      apiKey: apiKey,
      baseURL: apiUrl || undefined,
      dangerouslyAllowBrowser: true
    });

    // workaround openai o1* models do not accept system prompt
    if (model.startsWith("o1")) {
      messages.forEach(message => {
        if (message.role === "system") {
          message.role = "assistant";
        }
      });
      console.log(messages);
    }

    try {
      var req = {
        model: model,
        messages: messages,
        stream: false
      };

      if (apiUrl?.indexOf("openrouter") > -1) {
        req.include_reasoning = true;
      }

      const completion = await openai.chat.completions.create(req);
      return completion;
    } catch (error) {
      console.error(error);
      setErrorMessage(error.message || 'An error occurred while calling OpenAI')
      setIsErrorDialogOpen(true)
      throw error;
    }
  };

  async function appendJsonSample(prompt, mode) {
    // Find URLs starting with http:// or https://
    const urlRegex = /https?:\/\/[^\s"'<>)]+/g;
    const urls = prompt.match(urlRegex);

    if (!urls || urls.length === 0) {
      return prompt; // No URLs found, return original prompt
    }

    try {
      var jsonData = demoData.data || "{}";

      /* redundant? editorState.component.data is updated when urls are found
  
      if (mode == "generate" || (!sampleJson || sampleJson == "{}")) {
        // Fetch first URL found
        const response = await fetch(urls[0]);
        jsonData = await response.json();
  
        jsonData = JSON.stringify(jsonData, null, 2);
        editorState.component.data = jsonData;
      }
        */

      // Append JSON data to prompt
      const enhancedPrompt = `${prompt}\n\nSample data from ${demoData.dataUrl}:\n${jsonData}`;
      return enhancedPrompt;
    } catch (error) {
      console.error('Error fetching JSON data:', error);
      return prompt; // Return original prompt if fetch fails
    }
  }

  function supportsFileSystemAccess() {
    if ("showOpenFilePicker" in window) {
      try {
        return window.self === window.top;
      } catch {
        return false;
      }
    }
    return false;
  }

  const hasFileSystemSupport = supportsFileSystemAccess();

  return (
    <div className={`flex flex-col h-full ${className}`}>
      <div className="flex items-center gap-1 p-1 bg-gray-100 rounded flex-shrink-0">
        {(hasFileSystemSupport || editorState.component.id) && (
          <>
            {!editorState.component.id && hasFileSystemSupport && (
              <Button
                onClick={handleLoad}
                variant="toolbar"
                className="flex items-center gap-2 h-8"
                size="sm"
              >
                <Upload size={16} />
                Load
              </Button>
            )}
            {!editorState.component.id && hasFileSystemSupport && <div className="w-px h-4 bg-gray-300" />}
            <Button
              onClick={handleSave}
              variant="toolbar"
              className="flex items-center gap-2 h-8"
              size="sm"
            >
              <Save size={16} />
              Save
            </Button>
            <div className="w-px h-4 bg-gray-300" />
          </>
        )}
        <Button
          onClick={handleClear}
          variant="toolbar"
          className="flex items-center gap-2 h-8 text-red-500 hover:text-red-600"
          size="sm"
        >
          <Trash2 size={16} />
          Clear
        </Button>
        <div className="w-px h-4 bg-gray-300" />

        <SelectFromValueList
          options={models}
          value={modelName} // Use modelName from editorState
          placeholder="Select a model"
          onValueChange={newModelValue => { // Update editorState directly
            editorState.modelName = newModelValue;
          }}
        />

        <div className="w-px h-4 bg-gray-300" />

        <Button
          onClick={() => editorState.refresh()}
          variant="toolbar"
          className="flex items-center gap-2 h-8"
          size="sm"
        >
          <RefreshCw size={16} />
          Refresh
        </Button>

        <Button
          onClick={handleUndo}
          variant="toolbar"
          className="flex items-center gap-2 h-8"
          size="sm"
          disabled={undoDisabled}
        >
          <Undo size={16} />
          Undo
        </Button>

        <Button
          onClick={handleRedo}
          variant="toolbar"
          className="flex items-center gap-2 h-8"
          size="sm"
          disabled={redoDisabled}
        >
          <Redo size={16} />
          Redo
        </Button>

        {/* History Version Selector */}
        {history.versions.length > 1 && (
          <Select
            value={history.activeVersion.toString()}
            onValueChange={handleSelectHistoryVersion}
          >
            <SelectTrigger className="w-[120px] mr-2">
              <SelectValue placeholder="Version" />
            </SelectTrigger>
            <SelectContent>
              {history.versions.map((_, index) => (
                <SelectItem key={index} value={index.toString()}>
                  Version {index + 1}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <div className="flex items-center gap-2 ml-auto mr-2">
          <div className="flex items-center space-x-2">
            <Label htmlFor="demo-mode" className={demoData?.enabled ? "text-gray-800" : "text-gray-400"}>
              {demoData?.enabled ? "Demo" : "Live"}
            </Label>
            <Switch
              id="demo-mode"
              checked={demoData?.enabled || false}
              onCheckedChange={(checked) => {
                sessionState.demoData.enabled = checked;
              }}
            />
          </div>
        </div>

        <Button
          onClick={handleAPIConfigModalOpen}
          variant="toolbar"
          className="flex items-center gap-2 h-8"
          size="sm"
        >
          <Settings size={16} />
          Settings
        </Button>

      </div>
      <ResizablePanelGroup
        direction="horizontal"
        className="flex-1 min-h-0 mt-4"
      >
        {/* Left Panel */}
        <ResizablePanel defaultSize={33.33} minSize={20}>
          <ResizablePanelGroup direction="vertical" className="h-full">
            {/* Tabs Panel */}
            <ResizablePanel defaultSize={100}> {/* Adjusted defaultSize from 67 to 100 */}
              <div className="flex h-full flex-col px-4 py-4">
                <TabsPanel tabs={editTabs} />
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        </ResizablePanel>

        <ResizableHandle />

        {/* Combined Middle and Right Panel Area */}
        <ResizablePanel defaultSize={66.67} minSize={40}>
          <div className="flex flex-col h-full">
            {/* Fixed Top Panel for 3-way Toggle */}
            <div className="h-12 p-1 pb-4 flex items-center justify-start">
              <div className="flex gap-0 border rounded-md">
                <Button
                  size="sm"
                  variant={viewMode === 'editor' ? 'default' : 'ghost'}
                  onClick={() => setViewMode('editor')}
                  className="px-3 py-1 rounded-l-md rounded-r-none border-r"
                >
                  <Code size={16} />
                </Button>
                <Button
                  size="sm"
                  variant={viewMode === 'both' ? 'default' : 'ghost'}
                  onClick={() => setViewMode('both')}
                  className="px-3 py-1 rounded-none border-r"
                >
                  <SplitSquareHorizontal size={16} />
                </Button>
                <Button
                  size="sm"
                  variant={viewMode === 'preview' ? 'default' : 'ghost'}
                  onClick={() => setViewMode('preview')}
                  className="px-3 py-1 rounded-r-md rounded-l-none"
                >
                  <Eye size={16} />
                </Button>
              </div>
            </div>

            {/* Panel for Editor and/or Preview Content */}
            <div className="flex-1 min-h-0">
              <ResizablePanelGroup direction="horizontal" className="h-full">
                {viewMode === 'editor' && (
                  <ResizablePanel defaultSize={100} minSize={20}>
                    {isGenerating ? (
                      <EditorSkeleton />
                    ) : (
                      <MonacoEditor
                        value={jsx}
                        onChange={(value) => handleCodeChange(value)}
                        language="javascript" // Set language to 'javascript' for JSX support
                        theme={isDarkMode ? "vs-dark" : "vs-light"}
                        options={{
                          tabSize: 2,
                          minimap: { enabled: false },
                          scrollbar: {
                            vertical: 'auto',
                            horizontal: 'auto',
                            useShadows: true,
                            verticalScrollbarSize: 10,
                            horizontalScrollbarSize: 10
                          },
                          automaticLayout: true,
                        }}
                      />
                    )}
                  </ResizablePanel>
                )}

                {viewMode === 'preview' && (
                  <ResizablePanel defaultSize={100} minSize={20}>
                    <div className="px-4 h-full overflow-y-auto">
                      <Preview text={jsx} />
                    </div>
                  </ResizablePanel>
                )}

                {viewMode === 'both' && (
                  <>
                    <ResizablePanel defaultSize={50} minSize={20}>
                      {isGenerating ? (
                        <EditorSkeleton />
                      ) : (
                        <MonacoEditor
                          value={jsx}
                          onChange={(value) => handleCodeChange(value)}
                          language="javascript" // Set language to 'javascript' for JSX support
                          theme={isDarkMode ? "vs-dark" : "vs-light"}
                          options={{
                            tabSize: 2,
                            minimap: { enabled: false },
                            scrollbar: {
                              vertical: 'auto',
                              horizontal: 'auto',
                              useShadows: true,
                              verticalScrollbarSize: 10,
                              horizontalScrollbarSize: 10
                            },
                            automaticLayout: true,
                          }}
                        />
                      )}
                    </ResizablePanel>
                    <ResizableHandle />
                    <ResizablePanel defaultSize={50} minSize={20}>
                      <div className="px-4 h-full overflow-y-auto">
                        <Preview text={jsx} />
                      </div>
                    </ResizablePanel>
                  </>
                )}
              </ResizablePanelGroup>
            </div>
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
      <APIConfigModal
        open={isAPIConfigModalOpen}
        onClose={handleAPIConfigModalClose}
      />
      <AlertDialog open={isErrorDialogOpen} onOpenChange={setIsErrorDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Error</AlertDialogTitle>
            <AlertDialogDescription className="text-red-500">
              {errorMessage}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Close</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PlaygroundEditor;